import { useCallback, useEffect, useState } from 'react';

/**
 * URL-persisted filter state for shareable filtered list views.
 *
 * URL-persisted filter state. Operators sharing a filtered slice of a
 * list view (Quality NCs filtered to "Open + High-priority + Last 7
 * days") need to be able to copy the URL and send it. Without URL
 * persistence, filters live only in component state and the link
 * shows everyone the unfiltered list.
 *
 * The hook is the bridge between FilterBar's per-chip state and the
 * URL `?filter.*=...` query-string namespace. Each filter is keyed by
 * its FilterChipDef.id and serialized as `filter.{id}=value`.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * const [filters, setFilters] = useUrlFilterState({
 *   status: 'all',
 *   priority: 'all',
 *   range: '7d',
 * });
 *
 * <FilterBar
 *   filters={[
 *     {
 *       id: 'status',
 *       label: 'Status',
 *       value: filters.status,
 *       onClear: () => setFilters(prev => ({ ...prev, status: 'all' })),
 *       renderEditor: close => <StatusEditor onChange={s => { setFilters(p => ({ ...p, status: s })); close(); }} />,
 *     },
 *     // …more chips
 *   ]}
 * />
 * ```
 *
 * The hook reads from `window.location.search` on mount, writes back
 * through `history.replaceState` (no navigation), and listens for
 * back/forward via the `popstate` event. Server-side renders fall
 * back to defaults.
 */

export type FilterStateValue = string;

export interface UseUrlFilterStateOptions {
  /**
   * Query-string namespace prefix. Defaults to `filter.`. Two FilterBars
   * on the same page should pick distinct prefixes (e.g. `'a.filter.'`,
   * `'b.filter.'`) so they don't collide.
   */
  prefix?: string;
  /**
   * When true (default), values equal to the default are removed from
   * the URL so the canonical link is the shortest. When false, every
   * filter is always serialized.
   */
  pruneDefaults?: boolean;
}

/**
 * URL-persisted version of `useState<Record<string, string>>` keyed by
 * filter id. Initial defaults are taken from the URL on mount; missing
 * keys fall back to `defaults`.
 */
export function useUrlFilterState<T extends Record<string, FilterStateValue>>(
  defaults: T,
  options: UseUrlFilterStateOptions = {},
): [T, (next: T | ((prev: T) => T)) => void] {
  const prefix = options.prefix ?? 'filter.';
  const pruneDefaults = options.pruneDefaults ?? true;

  const readFromUrl = useCallback((): T => {
    if (typeof window === 'undefined') return defaults;
    const params = new URLSearchParams(window.location.search);
    const next: Record<string, FilterStateValue> = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const v = params.get(prefix + key);
      if (v != null) next[key] = v;
    }
    return next as T;
  }, [defaults, prefix]);

  const [state, setLocal] = useState<T>(readFromUrl);

  // Listen for popstate so back/forward navigation re-syncs filters.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setLocal(readFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [readFromUrl]);

  const setState = useCallback(
    (next: T | ((prev: T) => T)) => {
      setLocal(prev => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          // Strip every filter.* key first so removed filters don't linger.
          const toRemove: string[] = [];
          params.forEach((_, key) => {
            if (key.startsWith(prefix)) toRemove.push(key);
          });
          toRemove.forEach(k => params.delete(k));
          // Write each non-default key.
          for (const [key, value] of Object.entries(resolved)) {
            if (pruneDefaults && value === defaults[key]) continue;
            params.set(prefix + key, value);
          }
          const search = params.toString();
          const newUrl = `${window.location.pathname}${search ? '?' + search : ''}${window.location.hash}`;
          window.history.replaceState(window.history.state, '', newUrl);
        }
        return resolved;
      });
    },
    [defaults, prefix, pruneDefaults],
  );

  return [state, setState];
}
